import {
  convertDateToYYYYMMDDFormat,
  STATUSPAGE,
  SUBMIT_BUTTON,
} from '@repo/ui';
import { HandleStudentsNoAppUsageUseCaseInterface } from 'domain/usecases';
import { REPORT_TYPE } from 'domain/value-objects/report-type';
import { useFormik } from 'formik';
import { notifyError } from 'presentation/app/components/notify';
import { useLegends } from 'presentation/app/hooks/useLegends';
import { useService } from 'presentation/app/hooks/useService';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearStudentsNoAppUsageListData,
  loadStudentsNoAppUsageListData,
} from 'presentation/config/store/reports/students-no-app-usage-slice';
import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

export const useStudentsNoAppUsage = () => {
  // Hooks
  const { reportService } = useService();
  const dispatch = useDispatch();
  const { getLegends } = useLegends();

  // States
  const selectedSubmitBtnRef = useRef<SUBMIT_BUTTON | null>(null);
  const [loading, setLoading] = useState({
    loadingButtons: STATUSPAGE.DONE,
    loadingTable: STATUSPAGE.DONE,
  });
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const { studentsNoAppUsageListData, studentsNoAppUsageFilters } =
    useAppSelector((state) => state.studentsNoAppUsage);

  // Constants
  const todaysDate = new Date();
  const firstDayOfMonth = new Date(
    todaysDate.getFullYear(),
    todaysDate.getMonth(),
    1,
  );

  // Methods
  const toggleLoading = (key: string, status: STATUSPAGE) => {
    setLoading((prevState) => ({
      ...prevState,
      [key]: status,
    }));
  };

  const requestHandleStudentsNoAppUsagePDFOrExcel = useCallback(
    async (params: HandleStudentsNoAppUsageUseCaseInterface.Params) => {
      toggleLoading('loadingButtons', STATUSPAGE.LOADING);
      await reportService
        .handleStudentsNoAppUsage(params)
        .then((res: any) => {
          let blob = new Blob([res], {
            type: `application/${params?.reportType === REPORT_TYPE.PDF ? 'pdf' : 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'}`,
          });
          let link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          window.open(link.href, '_blank');
        })
        .catch((err) => {
          toggleLoading('loadingButtons', STATUSPAGE.ERROR);
          notifyError(err.message);
        })
        .finally(() => {
          toggleLoading('loadingButtons', STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestHandleStudentsNoAppUsageJSON = useCallback(
    async (params: HandleStudentsNoAppUsageUseCaseInterface.Params) => {
      toggleLoading('loadingButtons', STATUSPAGE.LOADING);
      toggleLoading('loadingTable', STATUSPAGE.LOADING);
      await reportService
        .handleStudentsNoAppUsage(params)
        .then((res) => dispatch(loadStudentsNoAppUsageListData(res)))
        .catch((err) => {
          dispatch(clearStudentsNoAppUsageListData());
          toggleLoading('loadingButtons', STATUSPAGE.ERROR);
          toggleLoading('loadingTable', STATUSPAGE.ERROR);
          notifyError(err.message);
        })
        .finally(() => {
          toggleLoading('loadingButtons', STATUSPAGE.DONE);
          toggleLoading('loadingTable', STATUSPAGE.DONE);
          setHasFetchedData(true);
        });
    },
    [],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      entity: studentsNoAppUsageFilters?.entity || null,
      course: studentsNoAppUsageFilters?.course || null,
      unit: studentsNoAppUsageFilters?.unit || null,
      groups: studentsNoAppUsageFilters?.groups || [],
      semester: studentsNoAppUsageFilters?.semester || null,
      starts:
        studentsNoAppUsageFilters?.starts ||
        convertDateToYYYYMMDDFormat(firstDayOfMonth),
      ends:
        studentsNoAppUsageFilters?.ends ||
        convertDateToYYYYMMDDFormat(todaysDate),
    },
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      starts: Yup.date().nullable().required('Data inicial é obrigatório'),
      ends: Yup.date().nullable().required('Data final é obrigatório'),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        entity: values?.entity?.value,
        course: values?.course?.value,
        curricularUnit: values?.unit?.value,
        semester: values?.semester?.value,
        groups: values?.groups?.map((group) => group.value),
        starts: values?.starts,
        ends: values?.ends,
      };

      switch (selectedSubmitBtnRef.current) {
        case SUBMIT_BUTTON.PDF:
          requestHandleStudentsNoAppUsagePDFOrExcel({
            ...valuesToSubmit,
            reportType: REPORT_TYPE.PDF,
          });
          break;
        case SUBMIT_BUTTON.EXCEL:
          requestHandleStudentsNoAppUsagePDFOrExcel({
            ...valuesToSubmit,
            reportType: REPORT_TYPE.EXCEL,
          });
          break;
        case SUBMIT_BUTTON.JSON:
          requestHandleStudentsNoAppUsageJSON({
            ...valuesToSubmit,
            reportType: REPORT_TYPE.JSON,
          });
          break;
        default:
          break;
      }
    },
  });

  return {
    // States
    loading,
    validation,
    hasFetchedData,
    selectedSubmitBtnRef,
    studentsNoAppUsageListData,
    // Methods
    getLegends,
    requestHandleStudentsNoAppUsagePDFOrExcel,
    requestHandleStudentsNoAppUsageJSON,
  };
};
